// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Fragment } from 'react';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useAccountsSelector } from './use-accounts-selector';
import {
  Divider,
  List,
  ListItemText,
  ListItem,
  ListSubheader,
  Popover,
  ListItemIcon,
  Checkbox,
  ListItemButton,
  Button,
} from '@mui/material';
import { AppIcon, Table, Translation } from '@shared/ui-kit';
import { AccountsSelectorTags } from './accounts-selector-tags';
import styles from './accounts-selector.module.css';

export const AccountsSelector = defineComponent({
  displayName: 'AccountsSelector',
  useSetup: useAccountsSelector,
  View: ({
    onClick,
    onClose,
    anchorEl,
    id,
    open,
    options,
    hasAllAccountsSelected,
    onSelectAllAccounts,
    onClearAllAccounts,
    onSelectInstitution,
    onSelectAccount,
    selectedInstitutions,
    accountSelected,
    onApplyChanges,
    selectedAccountsLabel,
    currency,
    selectAllLabel,
    tagOptions,
    onTagSelect,
    hasAccounts,
    canApply,
  }) => (
    <>
      <button
        onClick={onClick}
        aria-expanded={open}
        className={styles['accounts-button']}
        disabled={!hasAccounts}
        aria-disabled={!hasAccounts}
        data-testid="accounts-button"
      >
        <span className={styles['accounts-button-leading']}>
          <AppIcon
            icon="Funnel"
            color="inherit"
            size="base"
            className="shrink-0"
            data-testid="accounts-button-funnel-icon"
            aria-hidden
          />

          <span
            className={styles['accounts-button-text']}
            data-testid="accounts-button-text"
          >
            {selectedAccountsLabel}
          </span>
        </span>

        <AppIcon
          icon={open ? 'ArrowUp' : 'ArrowDown'}
          color="inherit"
          size="base"
          className="shrink-0"
          data-testid="accounts-button-icon"
          aria-expanded={open}
        />
      </button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{
          paper: styles['accounts-popover'],
        }}
        data-testid="accounts-popover"
      >
        <div
          className={styles['accounts-popover-subheader']}
          data-testid="accounts-popover-subheader"
        >
          <span data-testid="accounts-popover-subheader-text">
            <Translation i18nKey="aggregator.accounts" ns="aggregator" />
          </span>

          <button
            className={styles['accounts-popover-subheader-button']}
            data-testid="accounts-popover-subheader-button"
            aria-label={selectAllLabel}
            onClick={
              hasAllAccountsSelected ? onClearAllAccounts : onSelectAllAccounts
            }
          >
            {selectAllLabel}
          </button>
        </div>

        <div className={styles['accounts-popover-tags-section']}>
          <div className={styles['accounts-popover-tags-label']}>
            <AppIcon icon="Tag" size="xl" color="inherit" />
            <span className={styles['accounts-popover-tags-label-icon']}>
              <Translation i18nKey="common.views" ns="common" />
            </span>
          </div>

          <AccountsSelectorTags
            tagOptions={tagOptions}
            onTagSelect={onTagSelect}
            open={open}
            activeTagId={tagOptions.find(t => t.isActive)?.id ?? undefined}
          />
        </div>

        <Divider data-testid="accounts-popover-divider" />

        <List
          dense
          disablePadding
          className={styles['accounts-popover-list']}
          data-testid="accounts-popover-list"
        >
          {options.map((institution, index) => (
            <Fragment key={institution.value}>
              <ListSubheader
                disableGutters
                className={styles['accounts-popover-list-subheader-inner']}
                data-testid={`accounts-popover-list-subheader-${institution.value}`}
              >
                <ListItemButton
                  onClick={() => onSelectInstitution(institution.value)}
                  data-testid={`accounts-popover-list-subheader-${institution.value}-button`}
                >
                  <ListItemIcon className={styles['accounts-list-icon']}>
                    <Checkbox
                      checked={selectedInstitutions.has(institution.value)}
                      data-testid={`accounts-popover-list-subheader-${institution.value}-checkbox`}
                    />
                  </ListItemIcon>

                  <ListItemText
                    primary={institution.label}
                    primaryTypographyProps={{
                      className: styles['accounts-popover-subheader-text'],
                    }}
                    data-testid={`accounts-popover-list-subheader-${institution.value}-label`}
                  />
                </ListItemButton>
              </ListSubheader>

              <List
                dense
                disablePadding
                className={styles['accounts-popover-list-subheader-inner']}
                data-testid={`accounts-popover-list-${institution.value}`}
              >
                {institution.options.map(account => (
                  <ListItem
                    key={account.value}
                    disablePadding
                    data-testid={`accounts-popover-list-item-${account.value}`}
                  >
                    <ListItemButton
                      className={styles['accounts-list-item-button']}
                      onClick={() => onSelectAccount(account.value)}
                      data-testid={`accounts-popover-list-item-${account.value}-button`}
                    >
                      <ListItemIcon className={styles['accounts-list-icon']}>
                        <Checkbox
                          checked={accountSelected.has(account.value)}
                          data-testid={`accounts-popover-list-item-${account.value}-checkbox`}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary={account.label}
                        primaryTypographyProps={{
                          className: styles['accounts-popover-list-item-text'],
                        }}
                        secondary={
                          <span className={styles['accounts-secondary-text']}>
                            <span>
                              <Table.Data
                                format="currency"
                                value={account.balance}
                                validate
                                container
                                currency={currency}
                              />
                            </span>
                            <span>{account.iban}</span>
                          </span>
                        }
                        data-testid={`accounts-popover-list-item-${account.value}-label`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {index !== options.length - 1 && (
                <Divider
                  data-testid={`accounts-popover-list-divider-${institution.value}`}
                />
              )}
            </Fragment>
          ))}
        </List>

        <div
          className={styles['accounts-popover-footer']}
          data-testid="accounts-popover-footer"
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={onApplyChanges}
            className="w-full"
            disabled={!canApply}
            data-testid="accounts-popover-apply-button"
          >
            <Translation i18nKey="aggregator.apply" ns="aggregator" />
          </Button>
        </div>
      </Popover>
    </>
  ),
});
